import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { parseSpreadsheet, autoMap, FIELD_DEFS } from "@/lib/scParse";
import { MappingModal } from "@/components/sc/MappingModal";

// Reusable uploader for a single supply-chain document type.
export function UploadCard({ doctype, title, hint, accent = "#0047AB", hydrateRows = false, onUploaded }) {
  const inputRef = useRef(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(false);
  const [pending, setPending] = useState(null); // { columns, rows, filename }
  const [savedMap, setSavedMap] = useState({});

  const loadActive = async () => {
    try {
      const { data } = await api.get(`/sc/uploads`, { params: { doctype } });
      const cur = data[0] || null;
      setActive(cur);
      if (cur && hydrateRows) {
        const { data: full } = await api.get(`/sc/uploads/${cur.id}`);
        onUploaded?.({ upload: cur, rows: full.rows || [], mapping: full.mapping || {} });
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { loadActive(); }, [doctype]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const { columns, rows } = await parseSpreadsheet(file);
      if (!rows.length) { toast.error("That file has no rows."); return; }
      const { data } = await api.get(`/sc/mappings/${doctype}`);
      const merged = { ...autoMap(columns, FIELD_DEFS[doctype]), ...(data.mapping || {}) };
      setSavedMap(merged);
      setPending({ columns, rows, filename: file.name });
      setModal(true);
    } catch (err) {
      toast.error("Could not read file: " + (err?.message || err));
    } finally {
      setBusy(false);
    }
  };

  const confirmMapping = async (mapping) => {
    setModal(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/sc/uploads`, {
        doctype, filename: pending.filename, mapping,
        columns: pending.columns, rows: pending.rows,
        replace_id: active?.id || null,
      });
      setActive(data.upload);
      toast.success(`${title} imported (${pending.rows.length} rows).`);
      onUploaded?.({ upload: data.upload, rows: pending.rows, mapping });
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const remove = async () => {
    if (!active) return;
    try {
      await api.delete(`/sc/uploads/${active.id}`);
      setActive(null);
      onUploaded?.({ upload: null });
      toast.success(`${title} removed.`);
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`upload-card-${doctype}`}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 h-2.5 w-2.5 rounded-full shrink-0" style={{ background: accent }} />
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-sm text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
        </div>
      </div>

      {active ? (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-800 truncate" data-testid={`active-file-${doctype}`}>
              {active.filename}
            </div>
            <div className="text-[11px] text-slate-500">{active.row_count} rows</div>
          </div>
          <button onClick={() => inputRef.current?.click()} className="text-[11px] text-primary hover:underline" data-testid={`replace-${doctype}`}>
            Replace
          </button>
          <button onClick={remove} className="text-slate-400 hover:text-destructive" data-testid={`delete-${doctype}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`upload-btn-${doctype}`}
          className="mt-3 w-full flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-slate-200 hover:border-primary/50 hover:bg-slate-50 transition-colors py-5 text-slate-500"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
          <span className="text-xs font-medium">{busy ? "Reading…" : "Upload CSV or Excel"}</span>
          <span className="text-[10px] text-slate-400 flex items-center gap-1"><FileSpreadsheet className="w-3 h-3" /> .csv .xlsx .xls</span>
        </button>
      )}

      <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} />
      <MappingModal
        open={modal}
        onOpenChange={setModal}
        doctype={doctype}
        columns={pending?.columns || []}
        initialMapping={savedMap}
        onConfirm={confirmMapping}
      />
    </div>
  );
}
